import { FormattedText } from "@tm/static";
import { useEffect, useMemo, useState } from "react";
import { useCancelPolicyStep } from "../CancelPolicyContext";
import type { CancelPolicyRefundModeSelectorData } from "../types";
import {
  cardHeaderTitleStyle,
  cardSectionTitleStyle,
  ModeTag,
  RadioInner,
  RadioOuter,
  RefundModeCard,
  RefundModeContent,
  refundModeInfoStyle,
  refundModeLinkedWithStyle,
  RefundModeRadioWrap,
  RefundModeRow,
  RefundModes,
  refundModeTagStyle,
  RefundModeText,
  RefundModeTitleRow,
  StepCard,
} from "./CancelPolicyStepCards.style";

export const CancelPolicyRefundModeSelector = ({
  section,
  validation,
}: CancelPolicyRefundModeSelectorData) => {
  const { activeStepId, onStepStateChange, stepStateById } =
    useCancelPolicyStep();
  const visibleModes = useMemo(
    () =>
      section?.refundModes?.filter((mode) => mode.isVisible !== false) || [],
    [section?.refundModes]
  );
  const savedState = activeStepId
    ? stepStateById?.[activeStepId]?.value
    : undefined;
  const savedRefundModeId =
    typeof savedState?.["refundModeId"] === "string"
      ? savedState["refundModeId"]
      : "";
  const [selectedModeId, setSelectedModeId] = useState(
    () =>
      savedRefundModeId ||
      visibleModes.find((m) => m.selected && m.id)?.id ||
      ""
  );

  useEffect(() => {
    const next =
      savedRefundModeId ||
      visibleModes.find((m) => m.selected && m.id)?.id ||
      "";
    setSelectedModeId((prev) => (prev !== next ? next : prev));
  }, [savedRefundModeId, visibleModes]);

  const requiresSelection = validation?.isRequired !== false;
  const isValid = requiresSelection ? Boolean(selectedModeId) : true;

  useEffect(() => {
    onStepStateChange?.({
      isValid,
      value: { refundModeId: selectedModeId },
    });
  }, [isValid, onStepStateChange, selectedModeId]);

  return (
    <StepCard>
      <RefundModeContent>
        <FormattedText
          value={section?.title}
          baseStyle={cardSectionTitleStyle}
        />

        <RefundModes>
          {visibleModes.map((mode, index) => {
            const isSelected = selectedModeId === mode.id;

            const isDisabled = mode.isEnabled === false || !mode.id;

            return (
              <RefundModeCard
                key={mode.id || `mode-${index}`}
                type="button"
                $selected={isSelected}
                $disabled={isDisabled}
                disabled={isDisabled}
                onClick={() => mode.id && setSelectedModeId(mode.id)}
              >
                <RefundModeRow>
                  <RefundModeRadioWrap>
                    <RadioOuter $selected={isSelected}>
                      {isSelected ? <RadioInner /> : null}
                    </RadioOuter>
                  </RefundModeRadioWrap>

                  <RefundModeText>
                    <RefundModeTitleRow>
                      <FormattedText
                        value={mode.title}
                        baseStyle={cardHeaderTitleStyle}
                      />
                      {mode.tag?.title ? (
                        <ModeTag $type={mode.tag.type}>
                          <FormattedText
                            value={mode.tag.title}
                            baseStyle={refundModeTagStyle}
                          />
                        </ModeTag>
                      ) : null}
                    </RefundModeTitleRow>

                    {mode.linkedWith ? (
                      <FormattedText
                        value={mode.linkedWith}
                        baseStyle={refundModeLinkedWithStyle}
                      />
                    ) : null}

                    {mode.info ? (
                      <FormattedText
                        value={mode.info}
                        baseStyle={refundModeInfoStyle}
                      />
                    ) : null}
                  </RefundModeText>
                </RefundModeRow>
              </RefundModeCard>
            );
          })}
        </RefundModes>
      </RefundModeContent>
    </StepCard>
  );
};

export default CancelPolicyRefundModeSelector;
